/**
 * Smooth chart motion: the wheel tuning and the physics behind the gliding
 * wheel zoom, the go-to-latest glide and the price-scale ease that runs during
 * them. Pure math plus a self-stopping frame loop, with no klinecharts
 * dependency; ChartManager owns the glue (which gesture starts what, and how a
 * step is applied to the chart).
 *
 * A mouse drag has no momentum on purpose: a scrub back and forth should land
 * exactly where the pointer let go. (klinecharts keeps its own flick on touch.)
 *
 * Every animation here is time-based, not frame-based: easing uses
 * exponentials of elapsed milliseconds, so a 120 Hz display and a 60 Hz one
 * move the chart the same distance in the same time.
 */

/** Time constant of the wheel-zoom glide (ms). Larger is floatier. */
export const ZOOM_GLIDE_MS = 70
/** A glide counts as landed once it is within this fraction of its target. */
export const ZOOM_SETTLE_RATIO = 0.001
/** Wheel zoom per pixel of wheel travel: a 100 px mouse notch zooms by
 *  e^0.4, about x1.49, and a trackpad's stream of small deltas adds up to the
 *  same zoom for the same finger travel. */
export const WHEEL_ZOOM_PER_PX = 0.004
/** Time constant of the go-to-latest glide (ms). */
export const SCROLL_GLIDE_MS = 130
/** Time constant of the price-scale ease while the chart glides (ms). */
export const PRICE_GLIDE_MS = 80
/** A price scale counts as settled once both ends are within this fraction of
 *  the target span. */
export const PRICE_SETTLE_RATIO = 0.001
/** Price-axis wheel scale per pixel: a 100 px notch widens or narrows the
 *  range by 10%, the step a single notch always had. */
export const PRICE_WHEEL_PER_PX = Math.log(1.1) / 100
/** Largest wheel delta one event may apply (px), so a page-mode event can't
 *  jump the price axis. */
export const MAX_WHEEL_DELTA_PX = 300

/** Duration assumed for the first frame of a loop, before a real gap exists. */
const NOMINAL_FRAME_MS = 16
/** Longest frame gap the step ever sees (a backgrounded tab resumes here). */
const MAX_FRAME_MS = 64
/** `MotionLoop.handle` while a step runs, so a stop() inside it is final. */
const IN_STEP = -1

/** Exponential approach of `current` toward `target` over `dtMs`. */
export function easeToward(current: number, target: number, dtMs: number, tauMs: number): number {
  if (tauMs <= 0) return target
  return target + (current - target) * Math.exp(-dtMs / tauMs)
}

/** A wheel event's vertical delta in pixels. Line and page deltas convert the
 *  way klinecharts' own wheel handler does (32 and 120 px). */
export function wheelDeltaPx(deltaY: number, deltaMode: number): number {
  if (deltaMode === 1) return deltaY * 32
  if (deltaMode === 2) return deltaY * 120
  return deltaY
}

/** Bar-space multiplier for a wheel zoom of `deltaPx` (scroll up zooms in). */
export function wheelZoomFactor(deltaPx: number): number {
  return Math.exp(-deltaPx * WHEEL_ZOOM_PER_PX)
}

/** Price-range multiplier for a wheel over the price axis (scroll down widens
 *  the range, up narrows it), proportional to the delta and capped per event. */
export function priceWheelFactor(deltaPx: number): number {
  const capped = Math.max(-MAX_WHEEL_DELTA_PX, Math.min(MAX_WHEEL_DELTA_PX, deltaPx))
  return Math.exp(capped * PRICE_WHEEL_PER_PX)
}

/** Time source and frame scheduler, injectable so tests can step frames. */
export interface FrameClock {
  now(): number
  request(cb: () => void): number
  cancel(handle: number): void
}

export const browserFrameClock: FrameClock = {
  now: () => performance.now(),
  request: (cb) => requestAnimationFrame(() => cb()),
  cancel: (handle) => cancelAnimationFrame(handle),
}

/**
 * Runs `step(dtMs)` once per animation frame for as long as it returns true.
 * Idle costs nothing: no frame is requested once the step settles.
 */
export class MotionLoop {
  private handle: number | null = null
  /** Time of the previous frame; null until the first frame of a run. */
  private last: number | null = null

  constructor(
    private readonly step: (dtMs: number) => boolean,
    private readonly clock: FrameClock = browserFrameClock,
  ) {}

  get running(): boolean {
    return this.handle !== null
  }

  /** Starts the loop, or keeps the one already running. */
  start(): void {
    if (this.handle !== null) return
    this.last = null
    this.handle = this.clock.request(this.tick)
  }

  stop(): void {
    if (this.handle === null) return
    if (this.handle !== IN_STEP) this.clock.cancel(this.handle)
    this.handle = null
  }

  private readonly tick = (): void => {
    const now = this.clock.now()
    const dt = this.last === null ? NOMINAL_FRAME_MS : Math.min(now - this.last, MAX_FRAME_MS)
    this.last = now
    this.handle = IN_STEP
    const more = this.step(dt)
    // stop() (or stop() then start()) inside the step already decided what's next.
    if (this.handle !== IN_STEP) return
    this.handle = more ? this.clock.request(this.tick) : null
  }
}
